import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FaUtensils,
  FaEdit,
  FaCalendar,
  FaWeight,
  FaHeartbeat,
  FaTrash,
  FaPlus,
  FaSmile,
  FaHeadset,
  FaApple,
  FaGooglePlay,
  FaShieldAlt,
  FaStar,
  FaRocket,
  FaTrophy,
  FaUsers,
  FaChartLine,
} from 'react-icons/fa';
import { FunctionComponent } from 'react';
import logo from '../assets/logo.png'; // Replace with actual path to EatMeat logo
import { fetchApplicationSettings, ApplicationSettings } from '../lib/api';

interface Props {
  isLoggedIn: boolean;
}

// Scroll-triggered reveal using IntersectionObserver
const useInView = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, inView };
};

// Advanced animated feature card with scroll reveal + mouse-tracked 3D tilt
const AnimatedFeatureCard: React.FC<{
  Icon: FunctionComponent<{ className?: string }>;
  title: string;
  description: string;
  benefits: string[];
  delay: number;
  number: number;
}> = ({ Icon, title, description, benefits, delay, number }) => {
  const { ref, inView } = useInView();
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    // Detect touch device — disable 3D tilt on touch
    setIsTouch('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isTouch) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    setTilt({ x: y, y: x });
  };
  const handleMouseLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="feature-card-enhanced group group-card relative"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView
          ? `perspective(1200px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) translateY(0)`
          : 'perspective(1200px) rotateX(0deg) rotateY(0deg) translateY(40px)',
        transition: `opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1) ${delay}s, transform 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${inView ? '0s' : `${delay}s`}`,
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Number badge — smaller on mobile, positioned so it doesn't collide with title */}
      <div className="absolute top-3 right-3 w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-primary to-orange-500 text-white text-[10px] md:text-xs font-bold flex items-center justify-center shadow-lg z-10 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300">
        {String(number).padStart(2, '0')}
      </div>

      {/* Animated gradient border on hover */}
      <div className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
        background: 'linear-gradient(135deg, rgba(249,115,22,0.15) 0%, transparent 40%, transparent 60%, rgba(249,115,22,0.15) 100%)',
      }} />

      {/* Orbiting ring around icon on hover */}
      <div className="feature-icon-container relative" style={{ transform: 'translateZ(30px)' }}>
        <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
          background: 'conic-gradient(from 0deg, transparent 70%, rgba(249,115,22,0.4) 100%)',
          animation: 'spin 3s linear infinite',
        }} />
        <Icon className="text-primary h-16 w-16 card-icon relative z-10" />
        <div className="icon-glow"></div>
      </div>

      <h4 className="feature-title relative pr-10" style={{ transform: 'translateZ(20px)' }}>{title}</h4>
      <p className="feature-description relative">{description}</p>

      {/* Benefits with cascading reveal */}
      <ul className="feature-benefits">
        {benefits.map((benefit, bIndex) => (
          <li
            key={bIndex}
            className="benefit-item"
            style={{
              opacity: inView ? 1 : 0,
              transform: inView ? 'translateX(0)' : 'translateX(-12px)',
              transition: `opacity 0.5s ease-out ${delay + 0.3 + bIndex * 0.1}s, transform 0.5s ease-out ${delay + 0.3 + bIndex * 0.1}s`,
            }}
          >
            <div className="benefit-icon group-hover:scale-125 group-hover:rotate-180 transition-transform duration-500" style={{ transitionDelay: `${bIndex * 0.05}s` }}>
              {React.createElement(FaStar as any, { className: 'text-accent h-4 w-4' })}
            </div>
            <span className="benefit-text">{benefit}</span>
          </li>
        ))}
      </ul>
      <div className="feature-card-gradient"></div>

      {/* Shine sweep on hover */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-[400%] transition-transform duration-1000" />
      </div>
    </div>
  );
};

/* ═══ 33 Feature Preview Showcase — interactive tabbed mock ═══ */
const FeatureShowcase: React.FC = () => {
  const { ref, inView } = useInView(0.1);
  const [active, setActive] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  const features = [
    { cat: 'Recipes', name: '5,000+ Carnivore & Ketovore Recipes', icon: '🍖', color: '#ef4444', desc: 'From nose-to-tail carnivore classics to ketovore low-carb meals — all tested and macro-balanced.', mock: 'recipe' },
    { cat: 'Recipes', name: 'Recipe Search & Filter', icon: '🔍', color: '#f97316', desc: 'Filter by diet (carnivore/ketovore), cut, time, or macros. Find the perfect meal in seconds.', mock: 'search' },
    { cat: 'Recipes', name: 'Custom Recipe Editor', icon: '✏️', color: '#8b5cf6', desc: 'Build your own carnivore or ketovore recipes with ingredients, steps, and auto-calculated macros.', mock: 'editor' },
    { cat: 'Recipes', name: 'Recipe Sharing', icon: '📤', color: '#06b6d4', desc: 'Share creations with the community or keep them private to your meal plans.', mock: 'share' },
    { cat: 'Recipes', name: 'Thumbs Up/Down Voting', icon: '👍', color: '#10b981', desc: 'Vote on recipes. Community-favorite picks rise to the top of discovery.', mock: 'vote' },
    { cat: 'Tracking', name: 'Daily Macro Tracker', icon: '📊', color: '#ef4444', desc: 'Log protein, fat, and calories per meal. Works for both carnivore and ketovore macro targets.', mock: 'macro' },
    { cat: 'Tracking', name: 'Weight Progress', icon: '⚖️', color: '#3b82f6', desc: 'SVG line charts show weight trends over 7, 30, or 90 days on your carnivore or ketovore plan.', mock: 'weight' },
    { cat: 'Tracking', name: 'BMI Calculator', icon: '📏', color: '#06b6d4', desc: 'Auto-calc BMI from height & weight. Track body composition changes over time.', mock: 'bmi' },
    { cat: 'Tracking', name: 'Mood & Energy', icon: '😊', color: '#8b5cf6', desc: '10-level mood scale + energy slider. See how carnivore vs ketovore eating affects your mind.', mock: 'mood' },
    { cat: 'Tracking', name: 'Elimination Diet Log', icon: '🚫', color: '#f97316', desc: 'Track foods removed & reintroduced. Perfect for identifying triggers on either diet.', mock: 'elim' },
    { cat: 'Tracking', name: 'New Food Introduction', icon: '✅', color: '#10b981', desc: 'Safely add foods like avocado, cheese, or eggs. Monitor reactions while maintaining ketosis.', mock: 'intro' },
    { cat: 'Tracking', name: 'Period Cycle Log', icon: '🔴', color: '#ec4899', desc: 'Flow levels, start/end dates, symptoms — correlate with carnivore or ketovore diet changes.', mock: 'period' },
    { cat: 'Tracking', name: 'Daily Notes', icon: '📝', color: '#f59e0b', desc: 'Free-form observations. Your carnivore or ketovore journey in your own words.', mock: 'note' },
    { cat: 'Tracking', name: 'Streak Counter', icon: '🔥', color: '#ef4444', desc: 'Log consistently on your carnivore or ketovore plan and build streaks. Gentle motivation.', mock: 'streak' },
    { cat: 'Goals', name: 'Daily Macro Goals', icon: '🎯', color: '#f97316', desc: 'Set protein, fat, calorie targets tailored to carnivore or ketovore. Real-time progress rings.', mock: 'goals' },
    { cat: 'Goals', name: 'Weight Goals', icon: '🏁', color: '#3b82f6', desc: 'Set target weight. Track delta and estimated timeline on your chosen diet.', mock: 'wgoal' },
    { cat: 'Goals', name: 'Personal Motivation', icon: '💭', color: '#8b5cf6', desc: 'Write your "why". See it every time you log — stay focused on your carnivore or keto goals.', mock: 'motiv' },
    { cat: 'Goals', name: 'Reminders', icon: '🔔', color: '#10b981', desc: 'Daily or weekly nudges. Customizable. Never miss a meal log again.', mock: 'remind' },
    { cat: 'Meal Plans', name: 'Pre-Built Meal Plans', icon: '📅', color: '#f97316', desc: '21-day carnivore starter, 7-day ketovore reset, beginner and advanced plans — ready to follow.', mock: 'plans' },
    { cat: 'Meal Plans', name: 'Custom Meal Plans', icon: '🗓️', color: '#06b6d4', desc: 'Build your own multi-day carnivore or ketovore plans. Drag meals, auto-calc totals.', mock: 'cplans' },
    { cat: 'Meal Plans', name: 'Ingredient Catalog', icon: '🥩', color: '#ef4444', desc: '500+ ingredients with macros. Beef, pork, poultry, seafood, dairy, plus ketovore low-carb veggies.', mock: 'ingred' },
    { cat: 'Meal Plans', name: 'Shopping Lists', icon: '🛒', color: '#10b981', desc: 'Auto-generated from your meal plan. Grouped by category, checkable.', mock: 'shop' },
    { cat: 'Community', name: 'Community Feed', icon: '👥', color: '#3b82f6', desc: 'Share transformations, ask questions, get inspired by fellow carnivore and ketovore members.', mock: 'feed' },
    { cat: 'Community', name: 'Post Reactions', icon: '❤️', color: '#ec4899', desc: 'React with emojis. Comment. Connect. A real community, not a ghost town.', mock: 'react' },
    { cat: 'Community', name: 'Community Categories', icon: '📂', color: '#8b5cf6', desc: 'Beginners, Carnivore, Ketovore, Recipes, Transformations, Q&A — find your tribe.', mock: 'cats' },
    { cat: 'Community', name: 'Badges & Reputation', icon: '🏆', color: '#f59e0b', desc: 'Earn badges. Build reputation. Top members get highlighted.', mock: 'badges' },
    { cat: 'Community', name: 'Content Moderation', icon: '🛡️', color: '#10b981', desc: 'Every post reviewed by admins. Spam-free, drama-free, fact-based.', mock: 'mod' },
    { cat: 'Profile', name: 'Carnivore or Ketovore', icon: '🍽️', color: '#f97316', desc: 'Pick your diet — app tailors recipes, meal plans, and community content to match. Switch anytime.', mock: 'diet' },
    { cat: 'Profile', name: 'Favorite Foods', icon: '⭐', color: '#f59e0b', desc: 'Mark foods you love. Personalized carnivore or ketovore recipe recommendations.', mock: 'fav' },
    { cat: 'Profile', name: 'Favorite Recipes', icon: '💾', color: '#ef4444', desc: 'Save recipes you love from either diet. Quick access from your profile.', mock: 'favrec' },
    { cat: 'Profile', name: 'Profile Picture', icon: '📷', color: '#06b6d4', desc: 'Upload avatar. Personalize your community presence.', mock: 'avatar' },
    { cat: 'Support', name: '24/7 Live Chat', icon: '💬', color: '#10b981', desc: 'Real humans, real answers. Carnivore questions, ketovore guidance, tech help — anything.', mock: 'chat' },
    { cat: 'Support', name: 'Privacy-First', icon: '🔒', color: '#6366f1', desc: 'Your data stays yours. Secure auth, private meal logs, no ads, no tracking.', mock: 'privacy' },
  ];

  useEffect(() => {
    if (!autoPlay || !inView) return;
    const t = setInterval(() => setActive((a) => (a + 1) % features.length), 3500);
    return () => clearInterval(t);
  }, [autoPlay, inView, features.length]);

  // Scroll active feature into view *within* the left panel only (not the whole page)
  useEffect(() => {
    if (!autoPlay || !inView) return;
    const el = document.querySelector(`[data-feature-idx="${active}"]`) as HTMLElement | null;
    if (!el) return;
    // Find the scrollable container and scroll within it, not the page
    const container = el.closest('[data-showcase-list]') as HTMLElement | null;
    if (!container) return;
    const elTop = el.offsetTop;
    const elBottom = elTop + el.offsetHeight;
    const visibleTop = container.scrollTop;
    const visibleBottom = visibleTop + container.clientHeight;
    if (elTop < visibleTop || elBottom > visibleBottom) {
      container.scrollTo({ top: elTop - container.clientHeight / 2 + el.offsetHeight / 2, behavior: 'smooth' });
    }
  }, [active, autoPlay, inView]);

  const current = features[active];
  const categories = Array.from(new Set(features.map((f) => f.cat)));

  return (
    <section ref={ref} className="relative bg-white overflow-hidden border-y border-gray-100">
      {/* Ambient blobs */}
      <div className="pointer-events-none absolute -top-20 -left-20 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ backgroundColor: current.color, transition: 'background-color 1s ease', animation: 'pulse-slow 9s ease-in-out infinite' }} />
      <div className="pointer-events-none absolute bottom-0 -right-20 w-80 h-80 rounded-full blur-3xl opacity-20" style={{ backgroundColor: current.color, transition: 'background-color 1s ease', animation: 'pulse-slow 11s ease-in-out infinite 1s' }} />

      <div className="relative max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-10 fade-slide">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-semibold mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> 33 features · all in one app
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1]">
            Everything you need to <span className="shimmer-text">thrive</span>
          </h2>
          <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Tap any feature below to preview it. Hover to see the interactive mock animate.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* LEFT: feature list grouped by category — scrolls internally */}
          <div data-showcase-list className="lg:col-span-3 lg:max-h-[640px] lg:overflow-y-auto lg:pr-3 space-y-5 lg:[scrollbar-width:thin] lg:[scrollbar-color:#e5e7eb_transparent]">
            {categories.map((cat) => (
              <div key={cat}>
                <div className="sticky top-0 bg-white py-1 z-10 text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">{cat}</div>
                <div className="grid grid-cols-2 gap-2">
                  {features.map((f, idx) => f.cat !== cat ? null : (
                    <button
                      key={idx}
                      data-feature-idx={idx}
                      onClick={() => { setActive(idx); setAutoPlay(false); }}
                      onMouseEnter={() => { setActive(idx); setAutoPlay(false); }}
                      className={`group relative text-left rounded-xl border p-3 transition-all overflow-hidden ${active === idx ? 'border-primary bg-primary/5 shadow-md scale-[1.02]' : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'}`}
                    >
                      {active === idx && (
                        <div className="absolute inset-y-0 left-0 w-1 rounded-r-full" style={{ backgroundColor: f.color }} />
                      )}
                      <div className="flex items-start gap-2">
                        <div className="text-xl flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-transform">{f.icon}</div>
                        <div className="min-w-0 flex-1">
                          <div className={`text-xs font-bold truncate ${active === idx ? 'text-primary' : 'text-gray-800'}`}>{f.name}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{f.desc.split('.')[0]}</div>
                        </div>
                        {active === idx && <div className="text-primary text-xs">●</div>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT: sticky animated phone-style mock */}
          <div className="lg:col-span-2 lg:sticky lg:top-24 lg:max-h-[640px]">
            <div className="relative">
              {/* Auto-play indicator */}
              <div className="absolute -top-10 left-0 right-0 flex items-center justify-between text-xs">
                <div className="text-gray-400 font-semibold">
                  {active + 1}/{features.length} · {current.cat}
                </div>
                <button onClick={() => setAutoPlay(!autoPlay)} className="flex items-center gap-1.5 text-gray-500 hover:text-primary transition-colors">
                  <div className={`w-2 h-2 rounded-full ${autoPlay ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                  {autoPlay ? 'Auto' : 'Manual'}
                </button>
              </div>

              {/* Phone-style frame */}
              <div className="relative mx-auto bg-gradient-to-br from-gray-800 to-gray-900 rounded-[2.5rem] p-2 shadow-2xl" style={{ maxWidth: 320 }}>
                {/* Notch */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-900 rounded-full z-20" />
                <div className="relative bg-white rounded-[2rem] overflow-hidden" style={{ aspectRatio: '9/17' }}>
                  {/* Status bar */}
                  <div className="absolute top-0 inset-x-0 h-8 flex items-center justify-between px-6 text-[10px] font-bold text-gray-800 z-10">
                    <span>9:41</span>
                    <span className="flex items-center gap-1">
                      <span className="text-[8px]">●●●●</span>
                      <span>📶 100%</span>
                    </span>
                  </div>

                  {/* Header with active color */}
                  <div className="pt-10 pb-4 px-4 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${current.color} 0%, ${current.color}dd 100%)`, transition: 'background 0.6s ease' }}>
                    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, white 0%, transparent 40%)' }} />
                    <div key={active} className="relative" style={{ animation: 'fadeSlide 0.5s ease-out' }}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-2xl">{current.icon}</span>
                        <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{current.cat}</div>
                      </div>
                      <h3 className="text-lg font-extrabold leading-tight">{current.name}</h3>
                    </div>
                  </div>

                  {/* Body — feature-specific mock */}
                  <div className="px-4 py-4 h-[calc(100%-130px)] overflow-hidden">
                    <FeatureMock key={active} mock={current.mock} color={current.color} />
                  </div>

                  {/* Bottom nav */}
                  <div className="absolute bottom-0 inset-x-0 bg-white border-t border-gray-100 flex justify-around items-center py-2">
                    {['🏠', '📊', '🍖', '👥', '👤'].map((i, idx) => (
                      <div key={idx} className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg ${idx === 2 ? 'bg-primary/10 text-primary' : 'text-gray-400'}`}>{i}</div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Floating badges around phone */}
              <div className="absolute -left-6 top-1/4 bg-white rounded-xl border border-gray-100 shadow-lg px-3 py-2 text-[10px] font-bold flex items-center gap-1.5 animate-float-alt">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> Live
              </div>
              <div className="absolute -right-6 bottom-1/4 bg-white rounded-xl border border-gray-100 shadow-lg px-3 py-2 text-[10px] font-bold flex items-center gap-1.5 animate-float">
                ⚡ Instant
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Renders a distinct mini mock per feature type
const FeatureMock: React.FC<{ mock: string; color: string }> = ({ mock, color }) => {
  const animStyle = { animation: 'fadeSlide 0.6s ease-out' };

  if (mock === 'recipe' || mock === 'editor' || mock === 'share' || mock === 'vote' || mock === 'fav' || mock === 'favrec' || mock === 'ingred') return (
    <div style={animStyle} className="space-y-2">
      {[
        { n: 'Ribeye Butter', t: '20m', p: 68, diet: 'Carnivore', emoji: '🥩', bg: '#ef444422' },
        { n: 'Avocado-Bacon Bowl', t: '10m', p: 32, diet: 'Ketovore', emoji: '🥑', bg: '#10b98122' },
        { n: 'Pork Belly', t: '1h', p: 45, diet: 'Carnivore', emoji: '🥓', bg: '#ef444422' },
      ].map((r, i) => (
        <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg" style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.1}s backwards` }}>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg" style={{ background: r.bg }}>{r.emoji}</div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold text-gray-800 truncate">{r.n}</div>
            <div className="flex items-center gap-1 mt-0.5">
              <span className={`text-[8px] font-bold text-white px-1 py-0.5 rounded-full ${r.diet === 'Ketovore' ? 'bg-green-500' : 'bg-red-500'}`}>{r.diet}</span>
              <span className="text-[9px] text-gray-400">{r.t} · {r.p}g P</span>
            </div>
          </div>
          <span className="text-sm">{mock === 'vote' ? '👍' : mock === 'fav' || mock === 'favrec' ? '⭐' : '❤'}</span>
        </div>
      ))}
    </div>
  );

  if (mock === 'search') return (
    <div style={animStyle} className="space-y-2">
      <div className="bg-gray-50 rounded-lg p-2 flex items-center gap-2">🔍 <span className="text-xs text-gray-400">high-protein under 30m</span><span className="ml-auto text-[9px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold">42 results</span></div>
      <div className="flex gap-1 flex-wrap">
        <span className="text-[9px] bg-red-500 text-white rounded-full px-2 py-0.5 font-bold" style={{ animation: 'fadeSlide 0.4s ease-out 0.1s backwards' }}>🥩 Carnivore</span>
        <span className="text-[9px] bg-green-500 text-white rounded-full px-2 py-0.5 font-bold" style={{ animation: 'fadeSlide 0.4s ease-out 0.15s backwards' }}>🥑 Ketovore</span>
        {['<30m', 'High-protein'].map((f, i) => <span key={i} className="text-[9px] bg-gray-100 rounded-full px-2 py-0.5 font-semibold" style={{ animation: `fadeSlide 0.4s ease-out ${0.2 + i * 0.05}s backwards` }}>{f}</span>)}
      </div>
      {[
        { n: 'Ribeye Butter 20m', diet: 'Carnivore' },
        { n: 'Avocado-Bacon Bowl 10m', diet: 'Ketovore' },
        { n: 'Chicken Caesar Keto 18m', diet: 'Ketovore' },
      ].map((r, i) => (
        <div key={i} className="p-2 bg-gray-50 rounded-lg flex items-center justify-between" style={{ animation: `fadeSlide 0.5s ease-out ${0.25 + i * 0.08}s backwards` }}>
          <span className="text-xs font-semibold text-gray-800 truncate">{r.n}</span>
          <span className={`text-[8px] font-bold text-white px-1.5 py-0.5 rounded-full flex-shrink-0 ${r.diet === 'Ketovore' ? 'bg-green-500' : 'bg-red-500'}`}>{r.diet === 'Ketovore' ? '🥑' : '🥩'}</span>
        </div>
      ))}
    </div>
  );

  if (mock === 'macro' || mock === 'goals') {
    const rings = [{ p: 94, c: '#ef4444', l: 'Protein' }, { p: 73, c: '#f59e0b', l: 'Fat' }, { p: 81, c: '#f97316', l: 'Kcal' }];
    return (
      <div style={animStyle} className="grid grid-cols-3 gap-2 mt-2">
        {rings.map((r, i) => {
          const R = 26, C = 2 * Math.PI * R, off = C - (r.p / 100) * C;
          return (
            <div key={i} className="text-center">
              <div className="relative inline-flex items-center justify-center" style={{ width: 62, height: 62 }}>
                <svg width={62} height={62} className="-rotate-90">
                  <circle cx={31} cy={31} r={R} stroke="#f3f4f6" strokeWidth={5} fill="none" />
                  <circle cx={31} cy={31} r={R} stroke={r.c} strokeWidth={5} fill="none" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={off} style={{ animation: `drawRing 1.2s cubic-bezier(0.4,0,0.2,1) ${i * 0.1}s backwards` }} />
                </svg>
                <span className="absolute text-[10px] font-bold text-gray-800">{r.p}%</span>
              </div>
              <div className="text-[9px] font-semibold text-gray-500 mt-1">{r.l}</div>
            </div>
          );
        })}
      </div>
    );
  }

  if (mock === 'weight' || mock === 'wgoal' || mock === 'bmi') return (
    <div style={animStyle} className="space-y-2">
      <div className="flex justify-between items-center"><span className="text-[10px] font-bold text-gray-600">30-day trend</span><span className="text-[10px] font-bold text-green-600">↓ 3.2 kg</span></div>
      <svg viewBox="0 0 200 80" className="w-full">
        <defs><linearGradient id="wg3" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity="0.3" /><stop offset="100%" stopColor={color} stopOpacity="0" /></linearGradient></defs>
        <path d="M 0 30 L 30 35 L 60 25 L 90 40 L 120 32 L 150 45 L 180 42 L 200 55 L 200 80 L 0 80 Z" fill="url(#wg3)" />
        <path d="M 0 30 L 30 35 L 60 25 L 90 40 L 120 32 L 150 45 L 180 42 L 200 55" stroke={color} strokeWidth={2.5} fill="none" strokeLinecap="round" strokeDasharray={500} strokeDashoffset={500} style={{ animation: 'drawPath 1.8s ease-out forwards' }} />
      </svg>
      <div className="grid grid-cols-3 gap-1 pt-1">
        {[{l: 'Start', v: '82.4'}, {l: 'Now', v: '79.2'}, {l: 'Goal', v: '75.0'}].map((s, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-1.5 text-center"><div className="text-[9px] text-gray-400">{s.l}</div><div className="text-[11px] font-bold text-gray-800">{s.v}kg</div></div>
        ))}
      </div>
    </div>
  );

  if (mock === 'mood') return (
    <div style={animStyle} className="space-y-2">
      <div className="text-[10px] font-bold text-gray-600">This week</div>
      <div className="flex items-end justify-between gap-1 h-16">
        {[4, 6, 5, 8, 7, 9, 8].map((h, i) => (
          <div key={i} className="flex-1 rounded-sm origin-bottom" style={{ height: `${h * 10}%`, background: color, animation: `barGrow 0.6s ease-out ${i * 0.08}s backwards` }} />
        ))}
      </div>
      <div className="flex justify-between text-[9px] text-gray-400 px-0.5"><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span></div>
      <div className="grid grid-cols-5 gap-1 pt-2">
        {['😫', '😔', '😶', '🙂', '🤩'].map((e, i) => <div key={i} className={`p-2 rounded-lg text-center text-base ${i === 3 ? 'bg-primary/10 ring-2 ring-primary' : 'bg-gray-50'}`}>{e}</div>)}
      </div>
    </div>
  );

  if (mock === 'elim' || mock === 'intro') return (
    <div style={animStyle} className="space-y-1.5">
      {[
        { n: 'Dairy', s: mock === 'elim' ? 'Eliminated · 14d' : 'Added · 3d', ok: mock !== 'elim' },
        { n: 'Eggs', s: mock === 'elim' ? 'Eliminated · 7d' : 'Added · 1d', ok: mock !== 'elim' },
        { n: 'Coffee', s: mock === 'elim' ? 'Eliminated · 21d' : 'Reintroduced', ok: mock !== 'elim' },
      ].map((f, i) => (
        <div key={i} className="p-2 bg-gray-50 rounded-lg flex items-center gap-2" style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.1}s backwards` }}>
          <span className="text-lg">{f.ok ? '✅' : '🚫'}</span>
          <div className="flex-1 min-w-0"><div className="text-xs font-bold text-gray-800">{f.n}</div><div className="text-[9px] text-gray-400">{f.s}</div></div>
        </div>
      ))}
    </div>
  );

  if (mock === 'period') return (
    <div style={animStyle} className="space-y-2">
      <div className="bg-pink-50 rounded-lg p-2 flex items-center gap-2">🔴 <div className="flex-1"><div className="text-xs font-bold text-gray-800">Day 3 · Medium flow</div><div className="text-[9px] text-gray-400">28-day cycle</div></div></div>
      <div className="grid grid-cols-7 gap-0.5">
        {Array.from({ length: 28 }).map((_, i) => (
          <div key={i} className={`aspect-square rounded text-[8px] flex items-center justify-center ${i < 5 ? 'bg-pink-200 text-pink-700 font-bold' : i === 14 ? 'bg-primary/20 text-primary font-bold' : 'bg-gray-50 text-gray-400'}`} style={{ animation: `fadeSlide 0.3s ease-out ${i * 0.02}s backwards` }}>{i + 1}</div>
        ))}
      </div>
    </div>
  );

  if (mock === 'note' || mock === 'motiv') return (
    <div style={animStyle} className="space-y-2">
      {[
        { d: 'Today', t: mock === 'motiv' ? 'Stay consistent for 90 days' : 'Felt amazing after 3 days carnivore.' },
        { d: 'Yesterday', t: mock === 'motiv' ? 'Health, not numbers' : 'Slept 8 hours. Zero cravings.' },
      ].map((n, i) => (
        <div key={i} className="p-2 bg-gray-50 rounded-lg" style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.12}s backwards` }}>
          <div className="text-[9px] font-bold text-gray-400 uppercase">{n.d}</div>
          <div className="text-xs text-gray-700 italic mt-0.5">"{n.t}"</div>
        </div>
      ))}
    </div>
  );

  if (mock === 'streak') return (
    <div style={animStyle} className="text-center py-4">
      <div className="text-5xl mb-2" style={{ animation: 'floatUp 2s ease-in-out infinite' }}>🔥</div>
      <div className="text-3xl font-extrabold" style={{ color }}>12</div>
      <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Day streak</div>
      <div className="grid grid-cols-7 gap-1 mt-4">
        {[1,1,1,1,1,1,0].map((d, i) => <div key={i} className={`aspect-square rounded text-[8px] font-bold flex items-center justify-center ${d ? 'text-white' : 'bg-gray-100 text-gray-400'}`} style={{ backgroundColor: d ? color : undefined, animation: `fadeSlide 0.3s ease-out ${i * 0.05}s backwards` }}>{d ? '✓' : i + 1}</div>)}
      </div>
    </div>
  );

  if (mock === 'remind') return (
    <div style={animStyle} className="space-y-2">
      {['9:00 · Log breakfast', '13:00 · Mid-day check-in', '20:00 · Evening wrap-up'].map((r, i) => (
        <div key={i} className="p-2 bg-gray-50 rounded-lg flex items-center gap-2" style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.1}s backwards` }}>
          🔔 <span className="text-xs font-semibold text-gray-700 flex-1">{r}</span>
          <div className="w-7 h-4 rounded-full relative" style={{ background: color }}><div className="absolute right-0.5 top-0.5 w-3 h-3 rounded-full bg-white" /></div>
        </div>
      ))}
    </div>
  );

  if (mock === 'plans' || mock === 'cplans') return (
    <div style={animStyle} className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-500">
        <span>Day 3 of 21</span>
        <span className="bg-red-500 text-white px-1.5 py-0.5 rounded-full text-[8px]">🥩 Carnivore</span>
        <span className="text-gray-300">·</span>
        <span className="bg-green-500 text-white px-1.5 py-0.5 rounded-full text-[8px]">🥑 Ketovore</span>
      </div>
      {[
        { m: '🍳 Breakfast', r: 'Bacon & Eggs', k: 480, diet: 'Carnivore' },
        { m: '🥑 Lunch', r: 'Avocado-Chicken Bowl', k: 620, diet: 'Ketovore' },
        { m: '🍖 Dinner', r: 'Ribeye Steak', k: 720, diet: 'Carnivore' },
      ].map((m, i) => (
        <div key={i} className="p-2 bg-gray-50 rounded-lg flex items-center gap-2" style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.1}s backwards` }}>
          <span className="text-base">{m.m.split(' ')[0]}</span>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] text-gray-400">{m.m.split(' ')[1]}</div>
            <div className="text-xs font-bold text-gray-800 truncate">{m.r}</div>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <span className={`text-[8px] font-bold text-white px-1 py-0.5 rounded-full ${m.diet === 'Ketovore' ? 'bg-green-500' : 'bg-red-500'}`}>{m.diet === 'Ketovore' ? '🥑' : '🥩'}</span>
            <div className="text-[9px] font-bold text-gray-500">{m.k}kcal</div>
          </div>
        </div>
      ))}
    </div>
  );

  if (mock === 'shop') return (
    <div style={animStyle} className="space-y-1">
      <div className="text-[10px] font-bold text-gray-500 mb-1">Week 1 · 12 items</div>
      {['Ribeye steaks · 1kg', 'Pork belly · 500g', 'Farm eggs · 30ct', 'Grass-fed butter · 250g'].map((s, i) => (
        <div key={i} className="flex items-center gap-2 p-1.5 text-xs" style={{ animation: `fadeSlide 0.4s ease-out ${i * 0.08}s backwards` }}>
          <div className={`w-4 h-4 rounded border-2 flex-shrink-0 ${i < 2 ? 'border-primary bg-primary flex items-center justify-center' : 'border-gray-300'}`}>{i < 2 && <span className="text-white text-[8px]">✓</span>}</div>
          <span className={i < 2 ? 'line-through text-gray-400' : 'text-gray-700'}>{s}</span>
        </div>
      ))}
    </div>
  );

  if (mock === 'feed' || mock === 'react' || mock === 'cats' || mock === 'mod') return (
    <div style={animStyle} className="space-y-2">
      {[
        { u: 'Sarah M.', t: 'Down 15lbs on carnivore! 🔥', l: 42, tag: 'Carnivore', tagColor: 'bg-red-500' },
        { u: 'Mike R.', t: 'Ketovore avocado bowl recipe...', l: 27, tag: 'Ketovore', tagColor: 'bg-green-500' },
      ].map((p, i) => (
        <div key={i} className="p-2 bg-gray-50 rounded-lg" style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.1}s backwards` }}>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: color }}>{p.u[0]}</div>
            <span className="text-[10px] font-bold text-gray-800">{p.u}</span>
            <span className={`text-[8px] font-bold text-white px-1.5 py-0.5 rounded-full ${p.tagColor}`}>{p.tag}</span>
          </div>
          <div className="text-xs text-gray-700">{p.t}</div>
          <div className="flex gap-3 mt-1.5 text-[10px] text-gray-400"><span>❤ {p.l}</span><span>💬 {Math.floor(p.l / 3)}</span></div>
        </div>
      ))}
    </div>
  );

  if (mock === 'badges') return (
    <div style={animStyle} className="grid grid-cols-3 gap-2 mt-2">
      {[
        { e: '🏆', n: 'Top 1%' },
        { e: '🔥', n: '30-day' },
        { e: '💎', n: 'Pioneer' },
        { e: '📝', n: 'Author' },
        { e: '👑', n: 'Mentor' },
        { e: '⚡', n: 'Active' },
      ].map((b, i) => (
        <div key={i} className="p-2 bg-gray-50 rounded-lg text-center" style={{ animation: `fadeSlide 0.4s ease-out ${i * 0.06}s backwards` }}>
          <div className="text-2xl mb-0.5">{b.e}</div>
          <div className="text-[9px] font-bold text-gray-700">{b.n}</div>
        </div>
      ))}
    </div>
  );

  if (mock === 'diet') return (
    <div style={animStyle} className="space-y-2">
      {[
        { n: 'Carnivore', d: 'Animal foods only', sel: true, e: '🥩' },
        { n: 'Ketovore', d: 'Carnivore + low-carb plants', sel: false, e: '🥑' },
      ].map((d, i) => (
        <div key={i} className={`p-3 rounded-xl border-2 flex items-center gap-2 ${d.sel ? 'border-primary bg-primary/5' : 'border-gray-200 bg-gray-50'}`} style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.1}s backwards` }}>
          <span className="text-2xl">{d.e}</span>
          <div className="flex-1"><div className="text-xs font-bold text-gray-800">{d.n}</div><div className="text-[9px] text-gray-400">{d.d}</div></div>
          {d.sel && <span className="text-primary">✓</span>}
        </div>
      ))}
    </div>
  );

  if (mock === 'avatar') return (
    <div style={animStyle} className="flex flex-col items-center gap-3 py-3">
      <div className="w-24 h-24 rounded-full flex items-center justify-center text-4xl shadow-lg" style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}aa 100%)` }}>👤</div>
      <button className="text-[10px] font-bold bg-gray-100 rounded-full px-4 py-2">📷 Change photo</button>
      <div className="text-[10px] text-gray-400">Username: carnivore_king</div>
    </div>
  );

  if (mock === 'chat') return (
    <div style={animStyle} className="space-y-2">
      {[
        { u: false, t: "I'm new to carnivore. Where do I start?" },
        { u: true, t: 'Welcome! Start with our 21-day beginner plan.' },
        { u: false, t: "What about electrolytes?" },
        { u: true, t: "Great question! Salt liberally..." },
      ].map((m, i) => (
        <div key={i} className={`max-w-[80%] p-2 rounded-xl text-xs ${m.u ? 'bg-gray-100 text-gray-700 rounded-bl-sm' : 'ml-auto text-white rounded-br-sm'}`} style={{ background: m.u ? undefined : color, animation: `fadeSlide 0.4s ease-out ${i * 0.2}s backwards` }}>{m.t}</div>
      ))}
      <div className="flex items-center gap-2 pt-1">
        <div className="flex-1 bg-gray-100 rounded-full px-3 py-2 text-[10px] text-gray-400">Type a message…</div>
        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: color }}>↑</div>
      </div>
    </div>
  );

  if (mock === 'privacy') return (
    <div style={animStyle} className="space-y-2 text-center py-3">
      <div className="text-5xl" style={{ animation: 'floatUp 3s ease-in-out infinite' }}>🔒</div>
      <div className="text-sm font-bold text-gray-800">Your data stays yours</div>
      <div className="text-[10px] text-gray-500 px-4">End-to-end encryption on meal logs. No ads, no trackers, no sold data.</div>
      {['✅ Secure OAuth', '✅ Encrypted storage', '✅ GDPR compliant'].map((l, i) => (
        <div key={i} className="text-[10px] font-bold text-gray-600 text-left px-2" style={{ animation: `fadeSlide 0.5s ease-out ${i * 0.1 + 0.3}s backwards` }}>{l}</div>
      ))}
    </div>
  );

  return <div className="text-xs text-gray-400 text-center py-10">Preview</div>;
};

// ═══ Advanced animated final CTA section ═══
const AdvancedCTASection: React.FC<{ settings: ApplicationSettings }> = ({ settings }) => {
  const { ref, inView } = useInView(0.1);
  const [timeLeft, setTimeLeft] = useState({ h: 23, m: 59, s: 59 });

  // Live countdown (resets daily)
  useEffect(() => {
    const t = setInterval(() => {
      setTimeLeft((p) => {
        let { h, m, s } = p;
        s--;
        if (s < 0) { s = 59; m--; }
        if (m < 0) { m = 59; h--; }
        if (h < 0) { h = 23; m = 59; s = 59; }
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const pad = (n: number) => String(n).padStart(2, '0');

  const FaAppleI = FaApple as FunctionComponent<{ className?: string }>;
  const FaGooglePlayI = FaGooglePlay as FunctionComponent<{ className?: string }>;
  const FaShieldAltI = FaShieldAlt as FunctionComponent<{ className?: string }>;
  const FaStarI = FaStar as FunctionComponent<{ className?: string }>;
  const FaUsersI = FaUsers as FunctionComponent<{ className?: string }>;
  const FaRocketI = FaRocket as FunctionComponent<{ className?: string }>;

  return (
    <section ref={ref} className="relative overflow-hidden bg-gray-900 text-white py-16 md:py-24">
      {/* Ambient blobs */}
      <div className="pointer-events-none absolute -top-20 left-1/4 w-96 h-96 bg-primary rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 8s ease-in-out infinite' }} />
      <div className="pointer-events-none absolute -bottom-20 right-1/4 w-[28rem] h-[28rem] bg-red-500 rounded-full blur-3xl opacity-15" style={{ animation: 'pulse-slow 10s ease-in-out infinite 1s' }} />
      <div className="pointer-events-none absolute top-1/3 right-10 w-64 h-64 bg-green-500 rounded-full blur-3xl opacity-15" style={{ animation: 'pulse-slow 12s ease-in-out infinite 2s' }} />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="relative max-w-6xl mx-auto px-4 md:px-6">
        {/* Top banner — animated rocket + badge */}
        <div className="flex justify-center mb-6" style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(-20px)', transition: 'all 0.6s ease-out' }}>
          <div className="relative inline-flex items-center gap-2 bg-gradient-to-r from-primary to-orange-500 text-white rounded-full px-4 py-2 text-xs md:text-sm font-bold shadow-2xl shadow-primary/30">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary to-orange-500 blur-xl opacity-60 animate-pulse" />
            <span className="relative inline-flex" style={{ animation: 'floatUp 2s ease-in-out infinite' }}><FaRocketI className="h-4 w-4" /></span>
            <span className="relative">LIMITED TIME · 🎉 Free 21-day plan</span>
          </div>
        </div>

        {/* Heading */}
        <div className="text-center mb-8 md:mb-10" style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.7s ease-out 0.1s' }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-4">
            Start your <span className="shimmer-text">journey</span> today
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto px-2">
            Join 50,000+ members transforming their health with carnivore and ketovore meal planning.
          </p>
        </div>

        {/* Diet badges row */}
        <div className="flex justify-center gap-2 mb-8" style={{ opacity: inView ? 1 : 0, transition: 'opacity 0.6s ease-out 0.2s' }}>
          <div className="bg-red-500 rounded-full px-3 py-1.5 text-xs font-bold shadow-lg flex items-center gap-1.5">🥩 Carnivore</div>
          <div className="bg-green-500 rounded-full px-3 py-1.5 text-xs font-bold shadow-lg flex items-center gap-1.5">🥑 Ketovore</div>
          <div className="bg-white/10 backdrop-blur border border-white/20 rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5">✨ Both in one app</div>
        </div>

        {/* Countdown timer */}
        <div className="flex justify-center mb-10" style={{ opacity: inView ? 1 : 0, transform: inView ? 'scale(1)' : 'scale(0.9)', transition: 'all 0.6s ease-out 0.3s' }}>
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-3 md:p-4 flex items-center gap-2 md:gap-4">
            <div className="text-[10px] md:text-xs font-bold text-white/50 uppercase tracking-wider hidden sm:block">Offer ends in</div>
            {[
              { v: timeLeft.h, l: 'Hours' },
              { v: timeLeft.m, l: 'Min' },
              { v: timeLeft.s, l: 'Sec' },
            ].map((t, i) => (
              <React.Fragment key={i}>
                <div className="text-center">
                  <div className="relative">
                    <div className="text-2xl md:text-3xl font-extrabold text-white tabular-nums">{pad(t.v)}</div>
                    <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
                  </div>
                  <div className="text-[9px] md:text-[10px] font-bold text-white/40 uppercase tracking-wider mt-0.5">{t.l}</div>
                </div>
                {i < 2 && <div className="text-xl md:text-2xl font-extrabold text-primary animate-pulse">:</div>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Trust badges — horizontal row with icons */}
        <div className="grid grid-cols-3 gap-2 md:gap-4 max-w-3xl mx-auto mb-10">
          {[
            { icon: FaShieldAltI, title: 'Secure', sub: 'Your data is yours', color: 'bg-blue-500' },
            { icon: FaStarI, title: '4.8/5', sub: '50k+ reviews', color: 'bg-yellow-500' },
            { icon: FaUsersI, title: '50,000+', sub: 'Members', color: 'bg-green-500' },
          ].map((b, i) => (
            <div
              key={i}
              className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-3 md:p-4 flex flex-col md:flex-row items-center gap-2 md:gap-3 group hover:bg-white/10 transition-all duration-300"
              style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: `all 0.6s ease-out ${0.4 + i * 0.1}s` }}
            >
              <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl ${b.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-transform duration-300 shadow-lg`}>
                <b.icon className="h-4 w-4 md:h-5 md:w-5 text-white" />
              </div>
              <div className="text-center md:text-left min-w-0">
                <div className="text-sm md:text-base font-bold text-white truncate">{b.title}</div>
                <div className="text-[10px] md:text-xs text-white/50 truncate">{b.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA buttons — big with pulsing ring */}
        <div className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 mb-8">
          {[
            { href: settings.iosAppLink, icon: FaAppleI, sub: 'Download on the', label: 'App Store', primary: true, delay: 0.5 },
            { href: settings.androidAppLink, icon: FaGooglePlayI, sub: 'Get it on', label: 'Google Play', primary: false, delay: 0.6 },
          ].map((btn, i) => (
            <a
              key={i}
              href={btn.href}
              target="_blank"
              rel="noopener noreferrer"
              className="relative group flex-shrink-0"
              style={{ opacity: inView ? 1 : 0, transform: inView ? 'translateY(0)' : 'translateY(20px)', transition: `all 0.6s ease-out ${btn.delay}s` }}
            >
              {/* Pulsing ring for primary button */}
              {btn.primary && (
                <div className="absolute inset-0 rounded-2xl border-2 border-primary opacity-50" style={{ animation: 'pulse-slow 2s ease-in-out infinite' }} />
              )}
              <div className={`relative shine-btn overflow-hidden flex items-center gap-3 px-5 md:px-6 py-3 md:py-4 rounded-2xl font-bold transition-all hover:-translate-y-0.5 shadow-xl ${btn.primary ? 'bg-white text-gray-900 shadow-primary/30' : 'bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20'}`}>
                <btn.icon className="h-7 w-7 flex-shrink-0" />
                <div className="text-left">
                  <div className={`text-[10px] md:text-xs font-medium ${btn.primary ? 'text-gray-500' : 'text-white/60'}`}>{btn.sub}</div>
                  <div className="text-base md:text-lg font-extrabold leading-tight">{btn.label}</div>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Guarantee strip */}
        <div className="flex flex-wrap justify-center gap-x-4 md:gap-x-6 gap-y-2 text-xs md:text-sm text-white/60" style={{ opacity: inView ? 1 : 0, transition: 'opacity 0.6s ease-out 0.7s' }}>
          {['No credit card required', 'Instant access', 'Free forever', 'Cancel anytime'].map((g, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0"><span className="text-green-400 text-[10px] font-bold">✓</span></div>
              <span>{g}</span>
            </div>
          ))}
        </div>

        {/* Live signup ticker */}
        <div className="mt-10 max-w-md mx-auto">
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3" style={{ opacity: inView ? 1 : 0, transition: 'opacity 0.6s ease-out 0.8s' }}>
            <div className="relative w-2.5 h-2.5 flex-shrink-0">
              <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75" />
              <div className="relative w-2.5 h-2.5 bg-green-400 rounded-full" />
            </div>
            <div className="flex-1 h-5 relative overflow-hidden">
              {[
                { n: 'Sarah from Austin', a: 'just joined' },
                { n: 'Mike from Berlin', a: 'started carnivore' },
                { n: 'Priya from Mumbai', a: 'started ketovore' },
                { n: 'Alex from Toronto', a: 'just signed up' },
                { n: 'Emma from Sydney', a: 'joined today' },
              ].map((t, i) => (
                <div key={i} className="absolute inset-0 text-xs md:text-sm text-white/80 truncate" style={{ animation: `tickerUp 20s linear ${i * 4}s infinite`, opacity: 0 }}>
                  <strong className="text-white">{t.n}</strong>
                  <span className="text-white/50"> {t.a}</span>
                </div>
              ))}
            </div>
            <span className="text-[9px] md:text-[10px] font-bold text-green-400 uppercase tracking-wider flex-shrink-0">Live</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// Animated category header that slides in when scrolled into view
const AnimatedCategoryHeader: React.FC<{ title: string; index: number }> = ({ title, index }) => {
  const { ref, inView } = useInView(0.3);
  return (
    <div ref={ref} className="relative flex items-center justify-center mb-8 md:mb-12 px-4">
      <div
        className="absolute left-0 top-1/2 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'scaleX(1)' : 'scaleX(0)',
          transition: 'opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s',
        }}
      />
      <div
        className="relative bg-white px-3 md:px-8 py-2 flex items-center gap-2 md:gap-3 max-w-full"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.6s ease-out 0.1s, transform 0.6s ease-out 0.1s',
        }}
      >
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gradient-to-br from-primary to-orange-500 text-white text-sm md:text-base font-extrabold flex items-center justify-center shadow-lg flex-shrink-0">
          {index + 1}
        </div>
        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight text-center">{title}</h3>
      </div>
    </div>
  );
};

const defaultSettings: ApplicationSettings = {
  iosAppLink: 'https://apps.apple.com/app/id6746769046',
  androidAppLink: 'https://play.google.com/store/apps/details?id=com.carivore.lifestyle.eatmeat',
  heroTitle: '#1 Carnivore Diet App &\nKetovore Meal Planner',
  heroSubtitle: 'Transform your health with the ultimate carnivore diet tracker and ketovore meal planner. Access 5,000+ carnivore recipes, advanced tracking tools, meal prep guides, and 24/7 expert support.',
  ctaTitle: 'Start Your Carnivore Journey &\nKetovore Lifestyle Today!',
  ctaSubtitle: "Download the #1 carnivore diet app and ketovore meal planner on iOS and Android. Don't miss out—get your FREE 21-day carnivore meal plan, premium ketovore recipes, and advanced diet tracking tools!",
  promoText: '🎉 Free Carnivore & Ketovore Meal-Plans',
};

const Home: React.FC<Props> = React.memo(({ isLoggedIn }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStatIndex, setCurrentStatIndex] = useState(0);
  const [settings, setSettings] = useState<ApplicationSettings>(defaultSettings);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentStatIndex((prev) => (prev + 1) % 4);
    }, 3000);

    // Fetch application settings
    fetchApplicationSettings().then((data) => {
      setSettings({
        iosAppLink: data.iosAppLink || defaultSettings.iosAppLink,
        androidAppLink: data.androidAppLink || defaultSettings.androidAppLink,
        heroTitle: data.heroTitle || defaultSettings.heroTitle,
        heroSubtitle: data.heroSubtitle || defaultSettings.heroSubtitle,
        ctaTitle: data.ctaTitle || defaultSettings.ctaTitle,
        ctaSubtitle: data.ctaSubtitle || defaultSettings.ctaSubtitle,
        promoText: data.promoText || defaultSettings.promoText,
      });
    });

    return () => clearInterval(interval);
  }, []);

  // Define icon components with proper type casting
  const FaAppleIcon = FaApple as FunctionComponent<{ className?: string }>;
  const FaGooglePlayIcon = FaGooglePlay as FunctionComponent<{ className?: string }>;
  const FaShieldAltIcon = FaShieldAlt as FunctionComponent<{ className?: string }>;
  const FaStarIcon = FaStar as FunctionComponent<{ className?: string }>;
  const FaRocketIcon = FaRocket as FunctionComponent<{ className?: string }>;
  const FaTrophyIcon = FaTrophy as FunctionComponent<{ className?: string }>;
  const FaUsersIcon = FaUsers as FunctionComponent<{ className?: string }>;
  const FaChartLineIcon = FaChartLine as FunctionComponent<{ className?: string }>;

  const stats = [
    { icon: FaUsersIcon, number: '50,000+', text: 'Happy Users' },
    { icon: FaTrophyIcon, number: '4.8★', text: 'App Rating' },
    { icon: FaChartLineIcon, number: '5,000+', text: 'Carnivore Recipes' },
    { icon: FaUtensils as FunctionComponent<{ className?: string }>, number: '15lbs', text: 'Avg Weight Loss' },
  ];

  const featureCategories = [
    {
      category: 'Carnivore & Ketovore Diet Personalization',
      features: [
        {
          Icon: FaUtensils as FunctionComponent<{ className?: string }>,
          title: 'Carnivore Diet Recipes & Ketovore Meal Plans',
          description: 'Access premium carnivore recipes and ketovore meal plans tailored to your meat-based eating preferences. From nose-to-tail carnivore dishes to animal-based low-carb meals.',
          benefits: [
            'Over 5,000+ carnivore diet recipes and ketovore meal ideas',
            'Nose-to-tail carnivore cooking techniques and ancestral eating wisdom',
            'Customizable meal prep for your carnivore journey',
          ],
        },
        {
          Icon: FaEdit as FunctionComponent<{ className?: string }>,
          title: 'Personalized Carnivore Diet Tracker',
          description: 'Customize your carnivore diet plan with our advanced tracking tools. Perfect for elimination diets and identifying food sensitivities.',
          benefits: [
            'Track your carnivore eating plan and ketovore progress',
            'Elimination diet calendar for healing diet protocols',
            'Monitor carnivore benefits and ketosis levels',
          ],
        },
      ],
    },
    {
      category: 'Advanced Health & Weight Loss Tracking',
      features: [
        {
          Icon: FaWeight as FunctionComponent<{ className?: string }>,
          title: 'Weight Loss & Body Composition Monitoring',
          description: 'Track your weight loss naturally with our comprehensive carnivore diet tracker. Monitor BMI, body composition, and sustainable weight loss progress.',
          benefits: [
            'Lose weight naturally with carnivore diet tracking',
            'Monitor BMI and body composition changes',
            'Track energy levels and mental clarity improvements',
          ],
        },
        {
          Icon: FaHeartbeat as FunctionComponent<{ className?: string }>,
          title: 'Hormonal Health & Digestive Wellness',
          description: 'Log menstrual cycles and track how your carnivore or ketovore diet impacts hormonal balance and digestive health.',
          benefits: [
            'Correlate carnivore eating with hormonal improvements',
            'Track digestive health on meat-based eating plans',
            'Monitor well-being and nutrient density benefits',
          ],
        },
        {
          Icon: FaTrash as FunctionComponent<{ className?: string }>,
          title: 'Food Sensitivity & Elimination Tracking',
          description: 'Perfect for elimination diets - track foods removed from your carnivore or ketovore plan to identify sensitivities and optimize your healing diet.',
          benefits: [
            'Elimination diet tracking for food sensitivities',
            'Identify foods affecting your carnivore benefits',
            'Optimize your healing diet with detailed insights',
          ],
        },
        {
          Icon: FaPlus as FunctionComponent<{ className?: string }>,
          title: 'New Food Introduction Tracker',
          description: 'Safely introduce new foods to your carnivore or ketovore diet while monitoring their effects on your health goals and health goals.',
          benefits: [
            'Safely expand your carnivore eating plan',
            'Track energy levels with new food additions',
            'Monitor nutrient density and ketosis maintenance',
          ],
        },
        {
          Icon: FaSmile as FunctionComponent<{ className?: string }>,
          title: 'Mental Clarity & Mood Tracking',
          description: 'Track daily moods and mental clarity improvements from your carnivore diet journey and ketovore lifestyle.',
          benefits: [
            'Monitor mental clarity from carnivore benefits',
            'Track mood improvements on ketovore diet',
            'Correlate well-being with your meat-based eating plan',
          ],
        },
      ],
    },
    {
      category: 'Carnivore Meal Planning & Expert Support',
      features: [
        {
          Icon: FaCalendar as FunctionComponent<{ className?: string }>,
          title: 'Carnivore Meal Planner & Diet Calendar',
          description: 'Plan your carnivore meal prep and ketovore meals with our advanced diet calendar. Schedule your ancestral eating for optimal health goals.',
          benefits: [
            'Weekly carnivore meal plans and ketovore meal prep',
            'Diet calendar for consistent ancestral eating',
            'Add your own custom recipes to meal plans',
          ],
        },
        {
          Icon: FaHeadset as FunctionComponent<{ className?: string }>,
          title: 'Expert Carnivore Diet Support',
          description: 'Get 24/7 support for your carnivore diet questions, ketovore guidance, and dietary insights from our expert team.',
          benefits: [
            'Expert guidance for your carnivore journey',
            'Ketovore diet app support and troubleshooting',
            'Dietary insights for optimizing carnivore benefits',
          ],
        },
      ],
    },
    {
      category: 'Community, Macros & Recipe Sharing',
      features: [
        {
          Icon: FaUsers as FunctionComponent<{ className?: string }>,
          title: 'Private Carnivore Community',
          description: 'Join a thriving community of carnivore and ketovore enthusiasts. Share your journey, post transformations, ask questions, and get inspired by others.',
          benefits: [
            'Post updates with images and get reactions from the community',
            'Categories: Beginners, Recipes, Transformations, Q&A, Tips',
            'Moderated environment with badges for top members',
          ],
        },
        {
          Icon: FaChartLine as FunctionComponent<{ className?: string }>,
          title: 'Daily Macro Tracking',
          description: 'Track your protein, fat, and calorie intake per meal. Set daily goals and see real-time progress bars. View monthly stats with charts and trends.',
          benefits: [
            'Log macros per meal: Breakfast, Lunch, Dinner, Snack',
            'Set daily protein, fat, and calorie targets',
            'Calendar view with monthly stats and weight trends',
          ],
        },
        {
          Icon: FaEdit as FunctionComponent<{ className?: string }>,
          title: 'Create & Share Custom Recipes',
          description: 'Create your own carnivore recipes with full ingredients, instructions, and macros. Share them with the community or keep them private for your meal plans.',
          benefits: [
            'Full recipe editor with ingredients, steps, and nutrients',
            'Share recipes with the community or keep private',
            'Add your custom recipes directly to your meal plans',
          ],
        },
      ],
    },
  ];

  return (
    <div className="bg-caption-bg min-h-screen">
      <style>{`
        @keyframes floatUp { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-14px) rotate(3deg); } }
        @keyframes floatUpAlt { 0%,100% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-18px) rotate(-4deg); } }
        @keyframes fadeSlide { 0% { opacity: 0; transform: translateY(24px); } 100% { opacity: 1; transform: translateY(0); } }
        @keyframes fadeSlideLeft { 0% { opacity: 0; transform: translateX(-24px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes fadeSlideRight { 0% { opacity: 0; transform: translateX(24px); } 100% { opacity: 1; transform: translateX(0); } }
        @keyframes drawRing { 0% { stroke-dashoffset: 283; } 100% { stroke-dashoffset: 70; } }
        @keyframes drawPath { 0% { stroke-dashoffset: 500; } 100% { stroke-dashoffset: 0; } }
        @keyframes barGrow { 0% { transform: scaleY(0); } 100% { transform: scaleY(1); } }
        @keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
        @keyframes pulse-slow { 0%,100% { opacity: 0.3; } 50% { opacity: 0.6; } }
        @keyframes shine { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        @keyframes countUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes marqueeRev { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
        @keyframes tickerUp { 0%,100% { transform: translateY(100%); opacity: 0; } 4%,16% { transform: translateY(0); opacity: 1; } 20% { transform: translateY(-100%); opacity: 0; } 21%,100% { transform: translateY(100%); opacity: 0; } }
        .animate-float { animation: floatUp 5s ease-in-out infinite; }
        .animate-float-alt { animation: floatUpAlt 6.5s ease-in-out infinite; }
        .fade-slide { animation: fadeSlide 0.8s ease-out backwards; }
        .fade-left { animation: fadeSlideLeft 0.8s ease-out backwards; }
        .fade-right { animation: fadeSlideRight 0.8s ease-out backwards; }
        .shimmer-text { background: linear-gradient(90deg, #f97316 0%, #ef4444 30%, #f97316 60%, #ef4444 100%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: shimmer 6s linear infinite; }
        .shine-btn { position: relative; overflow: hidden; }
        .shine-btn::after { content: ''; position: absolute; top: 0; left: 0; width: 50%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent); animation: shine 3s infinite; }
        .count-animate { animation: countUp 0.6s ease-out backwards; }
        .group-card:hover .card-icon { transform: scale(1.1) rotate(6deg); }
        .card-icon { transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        .tilt-card { transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
        .tilt-card:hover { transform: perspective(1000px) rotateX(4deg) rotateY(-4deg) translateY(-4px); }
      `}</style>
      <Helmet>
        <title>EatMeat - #1 Carnivore Diet App & Ketovore Meal Planner | Lose Weight Naturally</title>
        <meta
          name="description"
          content="Transform your health with EatMeat - the #1 carnivore and ketovore meal planning app. 5,000+ tested recipes, macro tracking, custom recipe sharing, private community, elimination diet calendar, meal plans, and expert support. Free forever."
        />
        <meta
          name="keywords"
          content="carnivore diet app, ketovore meal planner, carnivore recipes, meat-based eating plan, carnivore diet tracker, ketovore diet, macro tracking, carnivore community, custom recipes, elimination diet, weight loss naturally, meal prep, health tracker"
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MobileApplication",
                "name": "EatMeat",
                "operatingSystem": ["iOS", "Android"],
                "applicationCategory": "Health & Fitness",
                "description": "EatMeat is the #1 carnivore diet app and ketovore meal planner, offering 5,000+ recipes, advanced tracking, meal planning, and 24/7 expert support.",
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.8",
                  "reviewCount": "50000"
                },
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                },
                "url": "https://www.eatmeatapp.com",
                "image": "https://www.eatmeatapp.com/assets/logo.png",
                "downloadUrl": [
                  settings.iosAppLink,
                  settings.androidAppLink
                ]
              },
              {
                "@type": "Organization",
                "name": "EatMeat",
                "url": "https://www.eatmeatapp.com",
                "logo": "https://www.eatmeatapp.com/assets/logo.png",
                "contactPoint": {
                  "@type": "ContactPoint",
                  "contactType": "Customer Support",
                  "url": "https://www.eatmeatapp.com/contact"
                },
                "sameAs": [
                  "https://www.facebook.com/profile.php?id=61576830232558",
                  "https://www.instagram.com/appeatmeat/",
                  "https://x.com/appeatmeat"
                ]
              },
              {
                "@type": "WebSite",
                "name": "EatMeat",
                "url": "https://www.eatmeatapp.com",
                "potentialAction": {
                  "@type": "SearchAction",
                  "target": "https://www.eatmeatapp.com/search?q={search_term_string}",
                  "query-input": "required name=search_term_string"
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* ═══ Modern Hero Section ═══ */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-orange-50/30 to-white">
        {/* Ambient blurred blobs */}
        <div className="pointer-events-none absolute -top-20 -left-20 w-[28rem] h-[28rem] bg-orange-200 rounded-full blur-3xl opacity-40" style={{ animation: 'pulse-slow 8s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-40 -right-20 w-[32rem] h-[32rem] bg-red-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 10s ease-in-out infinite 1s' }} />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-64 h-64 bg-yellow-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 12s ease-in-out infinite 2s' }} />

        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-10 lg:gap-16 items-center">
            {/* LEFT — copy */}
            <div className={`${isVisible ? 'fade-slide' : 'opacity-0'} text-center lg:text-left`} style={{ animationDelay: '0.1s' }}>
              {/* Promo pill */}
              <div className="inline-flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-2.5 py-1 text-[10px] md:text-xs font-semibold text-gray-700 mb-4 md:mb-5 shadow-sm max-w-full">
                <span className="relative w-1.5 h-1.5 flex-shrink-0">
                  <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-75" />
                  <span className="relative w-1.5 h-1.5 rounded-full bg-green-500 block" />
                </span>
                <span className="truncate">{settings.promoText.replace(/^🎉\s*/, '')}</span>
              </div>

              {/* Headline */}
              <h1 className="text-[2rem] leading-[1.1] sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-gray-900 tracking-tight sm:leading-[1.05] mb-3 md:mb-5">
                The <span className="shimmer-text">#1 Carnivore</span> <br className="hidden sm:block" />
                & Ketovore App
              </h1>

              {/* Subtitle */}
              <p className="text-sm md:text-lg text-gray-500 max-w-xl mx-auto lg:mx-0 mb-5 md:mb-6 leading-relaxed">
                Transform your health with <strong className="text-gray-800">5,000+ tested recipes</strong>, macro tracking, meal plans, and a thriving community.
              </p>

              {/* Live stats cycler — 2x2 grid on mobile, horizontal on desktop */}
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 md:gap-3 mb-5 md:mb-6 max-w-md mx-auto lg:mx-0">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-2 bg-white border rounded-xl px-2.5 py-2 transition-all duration-500 ${
                      index === currentStatIndex
                        ? 'border-primary shadow-md shadow-primary/10 scale-[1.03]'
                        : 'border-gray-100'
                    }`}
                  >
                    <stat.icon className={`h-4 w-4 flex-shrink-0 ${index === currentStatIndex ? 'text-primary' : 'text-gray-400'}`} />
                    <div className="min-w-0">
                      <div className="text-sm font-extrabold text-gray-900 leading-tight truncate">{stat.number}</div>
                      <div className="text-[9px] md:text-[10px] text-gray-500 font-semibold uppercase tracking-wider truncate">{stat.text}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Download buttons */}
              <div className="flex flex-col sm:flex-row gap-2.5 md:gap-3 max-w-md mx-auto lg:mx-0">
                <a
                  href={settings.iosAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden flex items-center justify-center gap-2.5 bg-gray-900 hover:bg-black text-white rounded-xl px-4 py-3 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex-1"
                  aria-label="Download EatMeat from the App Store"
                >
                  <FaAppleIcon className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />
                  <div className="text-left">
                    <div className="text-[9px] md:text-[10px] font-medium opacity-80">Download on the</div>
                    <div className="text-sm md:text-base font-extrabold leading-tight">App Store</div>
                  </div>
                  <div className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-[400%] transition-transform duration-1000" />
                </a>
                <a
                  href={settings.androidAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden flex items-center justify-center gap-2.5 bg-gradient-to-r from-primary to-orange-500 text-white rounded-xl px-4 py-3 shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all flex-1"
                  aria-label="Download EatMeat from Google Play"
                >
                  <FaGooglePlayIcon className="h-6 w-6 md:h-7 md:w-7 flex-shrink-0" />
                  <div className="text-left">
                    <div className="text-[9px] md:text-[10px] font-medium opacity-90">Get it on</div>
                    <div className="text-sm md:text-base font-extrabold leading-tight">Google Play</div>
                  </div>
                  <div className="absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-[400%] transition-transform duration-1000" />
                </a>
              </div>

              {/* Trust row */}
              <div className="mt-5 md:mt-8 flex flex-wrap justify-center lg:justify-start items-center gap-x-4 md:gap-x-6 gap-y-1.5 text-[11px] md:text-xs">
                <div className="flex items-center gap-1.5 text-gray-600">
                  <span className="text-yellow-500 text-sm">★★★★★</span>
                  <span className="font-semibold">4.8 rating</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500">
                  <FaUsersIcon className="h-3 w-3" />
                  <span>50,000+ members</span>
                </div>
                <div className="flex items-center gap-1.5 text-gray-500">
                  ✓ <span>Free forever</span>
                </div>
              </div>
            </div>

            {/* RIGHT — animated phone mock */}
            <div className={`${isVisible ? 'fade-slide' : 'opacity-0'} relative flex items-center justify-center px-8 sm:px-0`} style={{ animationDelay: '0.3s' }}>
              {/* Floating badges — smaller and closer-in on mobile */}
              <div className="absolute top-[3%] left-0 sm:-left-2 z-20 bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-xl px-2.5 py-2 md:px-4 md:py-3 flex items-center gap-1.5 md:gap-2 animate-float-alt">
                <div className="w-7 h-7 md:w-9 md:h-9 rounded-lg md:rounded-xl bg-green-50 flex items-center justify-center text-green-600 text-sm md:text-lg">✓</div>
                <div>
                  <div className="text-[9px] md:text-[10px] text-gray-400 font-semibold uppercase">Goal hit</div>
                  <div className="text-[11px] md:text-sm font-bold text-gray-800">150g protein</div>
                </div>
              </div>
              <div className="absolute top-[28%] right-0 sm:-right-2 z-20 bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-xl px-2.5 py-2 md:px-4 md:py-3 flex items-center gap-1.5 md:gap-2 animate-float">
                <span className="text-lg md:text-2xl">🔥</span>
                <div>
                  <div className="text-[9px] md:text-[10px] text-gray-400 font-semibold uppercase">Streak</div>
                  <div className="text-[11px] md:text-sm font-bold text-gray-800">12 days</div>
                </div>
              </div>
              <div className="absolute bottom-[22%] left-0 sm:-left-4 z-20 bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-xl px-2 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5 md:gap-2 animate-float">
                <span className="text-base md:text-xl">⚖️</span>
                <div>
                  <div className="text-[9px] md:text-[10px] text-gray-400 font-semibold uppercase">Lost</div>
                  <div className="text-[11px] md:text-sm font-bold text-green-600">↓ 3.2 kg</div>
                </div>
              </div>
              <div className="absolute bottom-[8%] right-0 sm:right-2 z-20 bg-gradient-to-br from-primary to-orange-500 text-white rounded-xl md:rounded-2xl shadow-xl px-2 py-1.5 md:px-3 md:py-2 flex items-center gap-1.5 md:gap-2 animate-float-alt">
                <div className="text-base md:text-xl font-extrabold">∞</div>
                <div>
                  <div className="text-[9px] md:text-[10px] font-bold uppercase tracking-wider opacity-90">Create</div>
                  <div className="text-[10px] md:text-[11px] font-extrabold">Unlimited</div>
                </div>
              </div>

              {/* App screenshot in phone frame */}
              <div className="relative mx-auto w-full phone-frame-responsive">
                {/* Soft orange glow behind the phone */}
                <div className="pointer-events-none absolute inset-0 rounded-[3rem] bg-primary/30 blur-3xl opacity-50 scale-90" />
                {/* Phone bezel */}
                <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2rem] md:rounded-[2.5rem] p-1.5 md:p-2 shadow-2xl">
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-gray-900 rounded-full z-20" />
                  <div className="relative rounded-[1.7rem] md:rounded-[2.2rem] overflow-hidden bg-white">
                    <picture>
                      <source srcSet="/screenshots/screen_shot.webp" type="image/webp" />
                      <img
                        src="/screenshots/screen_shot.jpg"
                        alt="EatMeat recipes screen"
                        className="w-full h-auto block"
                        loading="eager"
                        width={800}
                        height={1734}
                      />
                    </picture>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Recipe Preview Section (matches tracker preview pattern) ═══ */}
      <section className="relative bg-white overflow-hidden border-y border-gray-100">
        {/* Ambient blurred blobs */}
        <div className="pointer-events-none absolute -top-20 -right-20 w-80 h-80 bg-red-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 8s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-40 -left-20 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 10s ease-in-out infinite 1s' }} />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-64 h-64 bg-yellow-200 rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 12s ease-in-out infinite 2s' }} />

        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Animated recipe mock — LEFT this time for variety */}
            <div className="fade-left order-2 md:order-1" style={{ animationDelay: '0.2s' }}>
              <div className="relative">
                {/* Floating badges around card */}
                <div className="absolute -top-6 -left-4 z-20 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float-alt">
                  <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center text-red-500 text-lg">❤</div>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Saved</div>
                    <div className="text-sm font-bold text-gray-800">12.4k times</div>
                  </div>
                </div>
                <div className="absolute top-1/2 -right-4 z-20 bg-white rounded-2xl border border-gray-100 shadow-xl px-3 py-2 flex items-center gap-2 animate-float">
                  <span className="text-xl">⭐</span>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Rating</div>
                    <div className="text-sm font-bold text-gray-800">4.9/5</div>
                  </div>
                </div>
                <div className="absolute -bottom-4 left-8 z-20 bg-white rounded-2xl border border-gray-100 shadow-xl px-3 py-2 flex items-center gap-2 animate-float-alt">
                  <span className="text-xl">🥩</span>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Protein</div>
                    <div className="text-sm font-bold text-gray-800">68g</div>
                  </div>
                </div>

                {/* Main recipe card mock */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl shadow-gray-200/60 overflow-hidden tilt-card">
                  {/* Hero image stand-in — gradient "photo" */}
                  <div className="relative h-44 bg-gradient-to-br from-orange-300 via-red-400 to-red-600 flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 30% 30%, white 0%, transparent 40%)' }} />
                    <span className="text-7xl drop-shadow-2xl" style={{ animation: 'floatUp 4s ease-in-out infinite' }}>🥩</span>
                    {/* Diet pills */}
                    <div className="absolute top-3 left-3 flex gap-1.5 fade-slide" style={{ animationDelay: '0.5s' }}>
                      <div className="bg-white/90 backdrop-blur rounded-full px-2.5 py-1 text-[10px] font-bold text-gray-800 shadow-lg">Beef</div>
                      <div className="bg-red-500 text-white rounded-full px-2.5 py-1 text-[10px] font-bold shadow-lg">🥩 Carnivore</div>
                    </div>
                    {/* Save button */}
                    <button className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-red-500 shadow-lg hover:scale-110 transition-transform">❤</button>
                  </div>

                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="text-yellow-500 text-xs">★★★★★</div>
                      <div className="text-[10px] text-gray-400">4.9 · 2,431 reviews</div>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Ribeye with Garlic Butter</h3>
                    <p className="text-xs text-gray-500 mb-4">A perfectly seared ribeye basted with herb-infused butter. Ready in 20 minutes.</p>

                    {/* Info chips */}
                    <div className="flex gap-2 mb-4 text-[11px]">
                      <div className="bg-gray-50 rounded-lg px-2.5 py-1.5 flex items-center gap-1 font-semibold text-gray-700"><span>⏱</span> 20m</div>
                      <div className="bg-gray-50 rounded-lg px-2.5 py-1.5 flex items-center gap-1 font-semibold text-gray-700"><span>🍽</span> 2 servings</div>
                      <div className="bg-gray-50 rounded-lg px-2.5 py-1.5 flex items-center gap-1 font-semibold text-gray-700"><span>🔥</span> 612 kcal</div>
                    </div>

                    {/* Macro mini-bars that animate on load */}
                    <div className="space-y-2">
                      {[
                        { label: 'Protein', val: 68, max: 72, color: 'bg-red-500' },
                        { label: 'Fat', val: 42, max: 72, color: 'bg-yellow-500' },
                        { label: 'Carbs', val: 0, max: 72, color: 'bg-gray-400' },
                      ].map((m, i) => (
                        <div key={m.label} className="flex items-center gap-3">
                          <div className="w-14 text-[10px] font-semibold text-gray-500">{m.label}</div>
                          <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div className={`${m.color} h-full rounded-full origin-left`} style={{ width: `${(m.val / m.max) * 100}%`, animation: `fadeSlide 1s ease-out ${0.6 + i * 0.15}s backwards, barGrow 0.8s cubic-bezier(0.4,0,0.2,1) ${0.6 + i * 0.15}s backwards` }} />
                          </div>
                          <div className="text-[10px] font-bold text-gray-600 w-8 text-right">{m.val}g</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Stack behind — peek of 2nd recipe card */}
                <div className="absolute -z-10 inset-0 translate-x-3 translate-y-3 bg-white rounded-3xl border border-gray-100 shadow-lg opacity-60" />
                <div className="absolute -z-20 inset-0 translate-x-6 translate-y-6 bg-white rounded-3xl border border-gray-100 shadow-md opacity-30" />
              </div>
            </div>

            {/* Copy — RIGHT */}
            <div className="fade-right order-1 md:order-2" style={{ animationDelay: '0.1s' }}>
              <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 rounded-full px-3 py-1 text-xs font-semibold mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Recipe library · 2 diets
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1] mb-5">
                <span className="shimmer-text">5,000+</span> tested <br />
                carnivore & ketovore recipes
              </h2>
              <p className="text-lg text-gray-500 mb-6 max-w-md">From nose-to-tail carnivore classics to ketovore low-carb meals — each one macro-balanced and cook-tested.</p>

              {/* Live counter tiles */}
              <div className="grid grid-cols-2 gap-3 mb-6 max-w-md">
                {[
                  { n: '5,000+', l: 'Recipes total', icon: '🍖', tint: 'bg-red-50 text-red-600 border-red-100' },
                  { n: 'Both', l: 'Carnivore & Ketovore', icon: '🥩', tint: 'bg-orange-50 text-orange-600 border-orange-100' },
                  { n: '15m', l: 'Avg prep', icon: '⏱️', tint: 'bg-blue-50 text-blue-600 border-blue-100' },
                  { n: '100%', l: 'Diet-matched', icon: '✓', tint: 'bg-green-50 text-green-600 border-green-100' },
                ].map((s, i) => (
                  <div key={i} className={`relative rounded-2xl border ${s.tint} p-3 overflow-hidden group cursor-default fade-slide`} style={{ animationDelay: `${0.3 + i * 0.08}s` }}>
                    <div className="absolute -top-3 -right-3 text-5xl opacity-20 group-hover:scale-125 group-hover:rotate-12 transition-transform duration-500">{s.icon}</div>
                    <div className="relative">
                      <div className="text-2xl font-extrabold">{s.n}</div>
                      <div className="text-[10px] font-semibold mt-0.5 opacity-80">{s.l}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Diet filter chips */}
              <div className="flex gap-2 mb-3">
                <div className="bg-red-500 text-white rounded-full px-4 py-1.5 text-xs font-bold shadow-md flex items-center gap-1.5 fade-slide" style={{ animationDelay: '0.4s' }}>
                  🥩 Carnivore <span className="bg-white/20 rounded-full px-1.5 py-0.5 text-[9px]">1,200+</span>
                </div>
                <div className="bg-green-500 text-white rounded-full px-4 py-1.5 text-xs font-bold shadow-md flex items-center gap-1.5 fade-slide" style={{ animationDelay: '0.5s' }}>
                  🥑 Ketovore <span className="bg-white/20 rounded-full px-1.5 py-0.5 text-[9px]">800+</span>
                </div>
              </div>

              {/* Category pills */}
              <div className="flex flex-wrap gap-2 max-w-md">
                {['🥩 Beef', '🥓 Pork', '🍗 Poultry', '🐟 Fish', '🍳 Eggs', '🦴 Broths', '🍖 Lamb', '🥑 Low-carb', '+14 more'].map((c, i) => (
                  <div key={i} className="bg-gray-50 hover:bg-primary hover:text-white hover:border-primary border border-gray-200 text-gray-700 rounded-full px-3 py-1 text-xs font-semibold cursor-pointer transition-all duration-300 fade-slide" style={{ animationDelay: `${0.6 + i * 0.05}s` }}>{c}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Infinite scrolling recipe marquee — 2 rows, opposite directions */}
        <div className="pb-10 space-y-3">
          {[
            { dir: 'left', recipes: [
              { n: 'Ribeye with Garlic Butter', tag: 'Beef', diet: 'Carnivore', time: '20m', emoji: '🥩' },
              { n: 'Avocado-Bacon Bowl', tag: 'Low-carb', diet: 'Ketovore', time: '10m', emoji: '🥑' },
              { n: 'Pork Belly Cracklings', tag: 'Pork', diet: 'Carnivore', time: '1h', emoji: '🥓' },
              { n: 'Keto Cauliflower Steak', tag: 'Low-carb', diet: 'Ketovore', time: '25m', emoji: '🥦' },
              { n: 'Scrambled Eggs & Cheese', tag: 'Eggs', diet: 'Carnivore', time: '8m', emoji: '🍳' },
              { n: 'Lamb Chops Rosemary', tag: 'Lamb', diet: 'Carnivore', time: '25m', emoji: '🍖' },
              { n: 'Zucchini Beef Boats', tag: 'Low-carb', diet: 'Ketovore', time: '35m', emoji: '🥒' },
              { n: 'Beef Bone Broth', tag: 'Broth', diet: 'Carnivore', time: '8h', emoji: '🦴' },
              { n: 'Grass-Fed Burger', tag: 'Beef', diet: 'Carnivore', time: '12m', emoji: '🍔' },
              { n: 'Keto Chaffle Sandwich', tag: 'Low-carb', diet: 'Ketovore', time: '15m', emoji: '🥪' },
            ]},
            { dir: 'right', recipes: [
              { n: 'Wagyu Tartare', tag: 'Beef', diet: 'Carnivore', time: '10m', emoji: '🥩' },
              { n: 'Chicken Caesar Keto', tag: 'Low-carb', diet: 'Ketovore', time: '18m', emoji: '🥗' },
              { n: 'Salmon Fillet Seared', tag: 'Fish', diet: 'Carnivore', time: '12m', emoji: '🐟' },
              { n: 'Spinach-Feta Omelet', tag: 'Low-carb', diet: 'Ketovore', time: '10m', emoji: '🍳' },
              { n: 'Duck Breast Orange', tag: 'Poultry', diet: 'Carnivore', time: '22m', emoji: '🦆' },
              { n: 'Pork Shoulder Slow', tag: 'Pork', diet: 'Carnivore', time: '6h', emoji: '🍖' },
              { n: 'Shrimp Butter Garlic', tag: 'Seafood', diet: 'Carnivore', time: '10m', emoji: '🦐' },
              { n: 'Avocado-Tuna Salad', tag: 'Low-carb', diet: 'Ketovore', time: '8m', emoji: '🥑' },
              { n: 'Lamb Shank Braised', tag: 'Lamb', diet: 'Carnivore', time: '3h', emoji: '🍖' },
              { n: 'Keto Mushroom Sauté', tag: 'Low-carb', diet: 'Ketovore', time: '12m', emoji: '🍄' },
            ]},
          ].map((row, ri) => (
            <div key={ri} className="flex overflow-hidden group" style={{ maskImage: 'linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%)', WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 5%, black 95%, transparent 100%)' }}>
              <div
                className="flex gap-3 flex-shrink-0 pr-3"
                style={{
                  animation: `${row.dir === 'left' ? 'marquee' : 'marqueeRev'} ${45 + ri * 10}s linear infinite`,
                  width: 'max-content',
                }}
              >
                {[...row.recipes, ...row.recipes].map((recipe, i) => {
                  const isKeto = recipe.diet === 'Ketovore';
                  return (
                    <div
                      key={i}
                      className="group/card flex-shrink-0 w-64 bg-white rounded-2xl border border-gray-100 p-3 shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-primary/30 transition-all duration-300 cursor-pointer flex items-center gap-3"
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0 group-hover/card:scale-110 group-hover/card:rotate-6 transition-transform duration-300 ${isKeto ? 'bg-gradient-to-br from-green-100 to-emerald-100' : 'bg-gradient-to-br from-orange-100 to-red-100'}`}>
                        {recipe.emoji}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-sm text-gray-800 truncate">{recipe.n}</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full text-white ${isKeto ? 'bg-green-500' : 'bg-red-500'}`}>{isKeto ? '🥑' : '🥩'} {recipe.diet}</span>
                          <span className="text-[10px] text-gray-400">⏱ {recipe.time}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Live activity ticker */}
        <div className="relative max-w-3xl mx-auto px-6 pb-12">
          <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-2xl p-4 flex items-center gap-3 shadow-sm fade-slide" style={{ animationDelay: '0.3s' }}>
            <div className="relative w-3 h-3 flex-shrink-0">
              <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-75" />
              <div className="relative w-3 h-3 bg-green-500 rounded-full" />
            </div>
            <div className="flex-1 h-5 relative overflow-hidden">
              {[
                { name: 'Sarah', action: 'just saved', recipe: 'Ribeye with Garlic Butter' },
                { name: 'Mike', action: 'is cooking', recipe: 'Lamb Shank Braised' },
                { name: 'Jane', action: 'favorited', recipe: 'Grass-Fed Burger' },
                { name: 'Alex', action: 'logged', recipe: 'Bacon & Eggs Breakfast' },
                { name: 'Priya', action: 'just discovered', recipe: 'Pork Belly Cracklings' },
              ].map((t, i) => (
                <div
                  key={i}
                  className="absolute inset-0 text-sm text-gray-700 truncate"
                  style={{ animation: `tickerUp 20s linear ${i * 4}s infinite`, opacity: 0 }}
                >
                  <strong className="text-gray-900">{t.name}</strong>
                  <span className="text-gray-500"> {t.action} </span>
                  <span className="font-semibold text-primary">{t.recipe}</span>
                </div>
              ))}
            </div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex-shrink-0">Live</span>
          </div>
        </div>
      </section>

      {/* App Preview Section — animated dashboard mock */}
      <section className="relative bg-white overflow-hidden border-y border-gray-100">
        {/* Ambient blurred blobs */}
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 bg-red-200 rounded-full blur-3xl opacity-30" style={{ animation: 'pulse-slow 8s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute top-40 -right-20 w-96 h-96 bg-purple-200 rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 10s ease-in-out infinite 1s' }} />
        <div className="pointer-events-none absolute bottom-0 left-1/3 w-64 h-64 bg-blue-200 rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 12s ease-in-out infinite 2s' }} />

        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Copy */}
            <div className="fade-left" style={{ animationDelay: '0.1s' }}>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-semibold mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" /> Live dashboard preview
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1] mb-5">
                Built to make <span className="shimmer-text">daily tracking</span> effortless
              </h2>
              <p className="text-lg text-gray-500 mb-8 max-w-md">Progress rings, weight charts, mood distributions — every metric you care about, beautifully visualized.</p>
              <div className="space-y-3 max-w-md">
                {[
                  { icon: '🎯', title: 'Smart macro goals', desc: 'Set targets and watch rings fill as you log' },
                  { icon: '📈', title: 'Trend visualization', desc: 'SVG line charts reveal weekly and monthly patterns' },
                  { icon: '🗓️', title: 'Calendar at a glance', desc: 'Color dots for every log type — spot trends instantly' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl p-3 fade-slide" style={{ animationDelay: `${0.3 + i * 0.1}s` }}>
                    <div className="text-2xl flex-shrink-0">{f.icon}</div>
                    <div>
                      <div className="font-bold text-sm text-gray-800">{f.title}</div>
                      <div className="text-xs text-gray-500">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Animated dashboard mock */}
            <div className="fade-right" style={{ animationDelay: '0.2s' }}>
              <div className="relative">
                <div className="absolute -top-6 -left-4 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float-alt">
                  <div className="w-9 h-9 rounded-xl bg-green-50 flex items-center justify-center text-green-600 text-lg">✓</div>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Daily goal</div>
                    <div className="text-sm font-bold text-gray-800">93% complete</div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float">
                  <span className="text-2xl">🏆</span>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Week</div>
                    <div className="text-sm font-bold text-gray-800">7/7 logged</div>
                  </div>
                </div>

                <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl shadow-gray-200/60 p-6 tilt-card">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <div className="text-xs text-gray-400 font-medium">Today</div>
                      <div className="text-lg font-bold text-gray-800">Your progress</div>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <div className="w-2 h-2 rounded-full bg-yellow-400" />
                      <div className="w-2 h-2 rounded-full bg-green-400" />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-5">
                    {[
                      { label: 'Protein', val: '142g', pct: 94, color: '#ef4444', emoji: '🥩' },
                      { label: 'Fat', val: '88g', pct: 73, color: '#f59e0b', emoji: '🧈' },
                      { label: 'Calories', val: '1842', pct: 81, color: '#f97316', emoji: '🔥' },
                    ].map((m, i) => {
                      const r = 36, c = 2 * Math.PI * r, off = c - (m.pct / 100) * c;
                      return (
                        <div key={m.label} className="text-center">
                          <div className="relative inline-flex items-center justify-center" style={{ width: 88, height: 88 }}>
                            <svg width={88} height={88} className="-rotate-90">
                              <circle cx={44} cy={44} r={r} stroke="#f3f4f6" strokeWidth={7} fill="none" />
                              <circle cx={44} cy={44} r={r} stroke={m.color} strokeWidth={7} fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} style={{ animation: `drawRing 1.6s cubic-bezier(0.4,0,0.2,1) ${0.5 + i * 0.15}s backwards` }} />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className="text-base">{m.emoji}</span>
                              <span className="text-xs font-bold text-gray-800">{m.val}</span>
                            </div>
                          </div>
                          <div className="text-[10px] font-semibold text-gray-500 mt-1">{m.label}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-gray-50 rounded-xl p-3 mb-3">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-semibold text-gray-600">⚖️ Weight · 30d</span>
                      <span className="text-xs font-bold text-green-600">↓ 3.2 kg</span>
                    </div>
                    <svg viewBox="0 0 200 40" className="w-full" preserveAspectRatio="none" style={{ height: 40 }}>
                      <defs>
                        <linearGradient id="wgH" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path d="M 0 15 L 30 18 L 60 12 L 90 20 L 120 14 L 150 22 L 180 18 L 200 25 L 200 40 L 0 40 Z" fill="url(#wgH)" style={{ animation: 'fadeSlide 1.2s ease-out 0.6s backwards' }} />
                      <path d="M 0 15 L 30 18 L 60 12 L 90 20 L 120 14 L 150 22 L 180 18 L 200 25" stroke="#3b82f6" strokeWidth={2} fill="none" strokeLinecap="round" strokeDasharray={500} strokeDashoffset={500} style={{ animation: 'drawPath 2s ease-out 0.8s forwards' }} />
                    </svg>
                  </div>

                  <div className="flex items-end justify-between gap-1 h-10">
                    {[4, 6, 5, 8, 7, 9, 8].map((h, i) => (
                      <div key={i} className="flex-1 bg-gradient-to-t from-purple-400 to-purple-300 rounded-sm origin-bottom" style={{ height: `${h * 10}%`, animation: `barGrow 0.8s cubic-bezier(0.4,0,0.2,1) ${0.9 + i * 0.08}s backwards` }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ Calendar Preview Section ═══ */}
      <section className="relative bg-gradient-to-b from-white to-gray-50 overflow-hidden border-y border-gray-100">
        {/* Ambient blobs */}
        <div className="pointer-events-none absolute -top-20 -left-20 w-80 h-80 bg-purple-200 rounded-full blur-3xl opacity-25" style={{ animation: 'pulse-slow 9s ease-in-out infinite' }} />
        <div className="pointer-events-none absolute bottom-0 -right-20 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-20" style={{ animation: 'pulse-slow 11s ease-in-out infinite 1.5s' }} />

        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Copy — LEFT */}
            <div className="fade-left" style={{ animationDelay: '0.1s' }}>
              <div className="inline-flex items-center gap-2 bg-purple-50 text-purple-700 rounded-full px-3 py-1 text-xs font-semibold mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-pulse" /> Calendar view
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 tracking-tight leading-[1.1] mb-5">
                See your <span className="shimmer-text">month</span> at a glance
              </h2>
              <p className="text-lg text-gray-500 mb-6 max-w-md">Every day shows color dots for what you logged. Tap any day to jump straight to its entries — no scrolling, no searching.</p>

              {/* Legend */}
              <div className="space-y-2 max-w-md">
                {[
                  { color: 'bg-red-400', label: 'Macros logged', icon: '🥩' },
                  { color: 'bg-purple-400', label: 'Mood & energy', icon: '😊' },
                  { color: 'bg-blue-400', label: 'Weight tracked', icon: '⚖️' },
                  { color: 'bg-orange-400', label: 'Elimination diet', icon: '🚫' },
                  { color: 'bg-pink-400', label: 'Period cycle', icon: '🔴' },
                ].map((l, i) => (
                  <div key={i} className="flex items-center gap-3 fade-slide" style={{ animationDelay: `${0.3 + i * 0.08}s` }}>
                    <span className={`w-2.5 h-2.5 rounded-full ${l.color} flex-shrink-0`} />
                    <span className="text-xl">{l.icon}</span>
                    <span className="text-sm font-semibold text-gray-700">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Animated calendar mock — RIGHT */}
            <div className="fade-right" style={{ animationDelay: '0.2s' }}>
              <div className="relative">
                {/* Floating badges around calendar */}
                <div className="absolute -top-6 -left-4 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float-alt">
                  <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center text-purple-600 text-lg">📅</div>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Days tracked</div>
                    <div className="text-sm font-bold text-gray-800">23 / 30</div>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 z-10 bg-white rounded-2xl border border-gray-100 shadow-xl px-4 py-3 flex items-center gap-2 animate-float">
                  <span className="text-2xl">🔥</span>
                  <div>
                    <div className="text-[10px] text-gray-400 font-semibold uppercase">Streak</div>
                    <div className="text-sm font-bold text-gray-800">12 days</div>
                  </div>
                </div>

                {/* Calendar mock */}
                <div className="bg-white rounded-3xl border border-gray-100 shadow-2xl shadow-gray-200/60 p-5 tilt-card mx-auto" style={{ maxWidth: 380 }}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-sm font-bold text-gray-800">April 2026</div>
                      <div className="text-[10px] text-gray-400">23 days logged this month</div>
                    </div>
                    <div className="flex gap-1">
                      <button className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-400 text-sm transition-colors">‹</button>
                      <button className="w-7 h-7 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-400 text-sm transition-colors">›</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-1.5 mb-1">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => <div key={idx} className={`text-center text-[10px] font-bold py-1 ${idx === 0 || idx === 6 ? 'text-gray-300' : 'text-gray-400'}`}>{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1.5">
                    {Array.from({ length: 30 }).map((_, idx) => {
                      const patterns: Record<number, string[]> = {
                        0: ['bg-red-400', 'bg-purple-400'],
                        1: ['bg-red-400'],
                        2: ['bg-red-400', 'bg-blue-400'],
                        3: ['bg-red-400', 'bg-purple-400', 'bg-blue-400'],
                        4: ['bg-red-400'],
                        5: ['bg-purple-400', 'bg-orange-400'],
                        7: ['bg-red-400', 'bg-purple-400'],
                        8: ['bg-red-400', 'bg-blue-400'],
                        9: ['bg-red-400', 'bg-purple-400', 'bg-pink-400'],
                        10: ['bg-red-400', 'bg-pink-400'],
                        11: ['bg-pink-400'],
                        12: ['bg-red-400', 'bg-purple-400'],
                        14: ['bg-red-400', 'bg-orange-400'],
                        15: ['bg-red-400', 'bg-purple-400', 'bg-blue-400'],
                        17: ['bg-red-400'],
                        18: ['bg-red-400', 'bg-purple-400'],
                        19: ['bg-red-400', 'bg-blue-400'],
                        20: ['bg-red-400', 'bg-purple-400', 'bg-orange-400'],
                        21: ['bg-red-400', 'bg-purple-400'],
                        23: ['bg-red-400'],
                        24: ['bg-red-400', 'bg-purple-400'],
                        25: ['bg-red-400', 'bg-blue-400'],
                      };
                      const dots = patterns[idx] || [];
                      const isToday = idx === 16;
                      const isSelected = idx === 22;
                      return (
                        <button
                          key={idx}
                          className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-xs transition-transform hover:scale-110 ${
                            isToday ? 'bg-gradient-to-br from-primary to-orange-500 text-white font-bold shadow-md' :
                            isSelected ? 'bg-primary/10 text-primary font-bold ring-2 ring-primary' :
                            dots.length ? 'bg-gray-50 text-gray-700 hover:bg-gray-100' : 'text-gray-300'
                          }`}
                          style={{ animation: `fadeSlide 0.5s ease-out ${idx * 0.015}s backwards` }}
                        >
                          <span className="text-[11px]">{idx + 1}</span>
                          {dots.length > 0 && !isToday && (
                            <div className="absolute bottom-1 flex gap-0.5">
                              {dots.slice(0, 4).map((d, di) => (
                                <span key={di} className={`w-1 h-1 rounded-full ${d}`} style={{ animation: `fadeSlide 0.3s ease-out ${idx * 0.015 + 0.3 + di * 0.05}s backwards` }} />
                              ))}
                            </div>
                          )}
                          {isToday && <span className="absolute bottom-0.5 text-[7px]">Today</span>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Selected day preview */}
                  <div className="mt-4 pt-4 border-t border-gray-100" style={{ animation: 'fadeSlide 0.6s ease-out 1s backwards' }}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-bold text-gray-800">April 23 · 3 logs</div>
                      <span className="text-[10px] text-primary font-semibold">View all →</span>
                    </div>
                    <div className="space-y-1.5">
                      {[
                        { emoji: '🥩', label: 'Macro Log', sub: '142g P · 88g F · 1842 kcal', dot: 'bg-red-400' },
                        { emoji: '😊', label: 'Mood Log', sub: 'Energized · 8/10', dot: 'bg-purple-400' },
                        { emoji: '⚖️', label: 'Weight Track', sub: '79.2 kg', dot: 'bg-blue-400' },
                      ].map((e, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg" style={{ animation: `fadeSlide 0.5s ease-out ${1.1 + i * 0.1}s backwards` }}>
                          <span className="text-base">{e.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-bold text-gray-800">{e.label}</div>
                            <div className="text-[10px] text-gray-400 truncate">{e.sub}</div>
                          </div>
                          <span className={`w-1.5 h-1.5 rounded-full ${e.dot}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 33 Features Interactive Showcase */}
      <FeatureShowcase />

      {/* Features Section */}
      <section className="features-section-enhanced py-12 md:py-24">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <div className="features-badge mb-4 md:mb-6 inline-flex items-center">
              <FaTrophyIcon className="mr-2 h-4 w-4 md:h-5 md:w-5" />
              AWARD-WINNING APP
            </div>
            <h2 className="features-heading mb-4 md:mb-6">
              Why EatMeat is the Ultimate <span className="md:hidden text-primary">Carnivore & Ketovore App</span>
              <span className="hidden md:inline"><br/><span className="text-primary">Carnivore Diet App & Ketovore Meal Planner</span></span>
            </h2>
            <p className="features-subtitle max-w-4xl mx-auto px-2">
              <span className="md:hidden">Join 50,000+ users trusting EatMeat for their carnivore or ketovore journey, with tracking, meal plans, and community in one app.</span>
              <span className="hidden md:inline">Discover why over 50,000 users trust EatMeat for their carnivore journey, ketovore lifestyle, and sustainable weight loss goals. Our comprehensive meat-based eating platform combines ancestral eating wisdom with modern tracking technology.</span>
            </p>
          </div>
          {featureCategories.map((category, catIndex) => (
            <div key={catIndex} className="mb-12 md:mb-20">
              <AnimatedCategoryHeader title={category.category} index={catIndex} />
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6 mt-8 md:mt-12">
                {category.features.map((f, index) => (
                  <AnimatedFeatureCard
                    key={index}
                    Icon={f.Icon}
                    title={f.title}
                    description={f.description}
                    benefits={f.benefits}
                    delay={index * 0.12}
                    number={catIndex * 10 + index + 1}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="testimonial-section py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="testimonial-badge mb-6">
              <FaUsersIcon className="mr-2 h-5 w-5" />
              50,000+ SUCCESS STORIES
            </div>
            <h2 className="testimonial-heading mb-6">
              Join the Carnivore & Ketovore<br/>
              <span className="text-primary">Success Community!</span>
            </h2>
            <p className="testimonial-subtitle">
              Real transformations from our carnivore diet app and ketovore meal planner users
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mt-8 md:mt-12">
            <div className="testimonial-card featured fade-slide tilt-card" style={{ animationDelay: '0.1s' }}>
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <FaStarIcon key={i} className="star-icon" />
                ))}
              </div>
              <blockquote className="testimonial-quote">
                "EatMeat's carnivore diet tracker made my meat-based eating effortless—I've lost 15 lbs in 2 months and gained incredible mental clarity!"
              </blockquote>
              <div className="testimonial-author">
                <div className="author-avatar">J</div>
                <div>
                  <div className="author-name">Jane D.</div>
                  <div className="author-title">Fitness Enthusiast</div>
                </div>
              </div>
              <div className="testimonial-stats">
                <div className="stat">
                  <span className="stat-number">15</span>
                  <span className="stat-label">lbs lost</span>
                </div>
                <div className="stat">
                  <span className="stat-number">2</span>
                  <span className="stat-label">months</span>
                </div>
              </div>
            </div>
            <div className="testimonial-card fade-slide tilt-card" style={{ animationDelay: '0.25s' }}>
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <FaStarIcon key={i} className="star-icon" />
                ))}
              </div>
              <blockquote className="testimonial-quote">
                "The ketovore meal planner and carnivore recipes saved me hours every week. The meal prep feature is amazing!"
              </blockquote>
              <div className="testimonial-author">
                <div className="author-avatar">J</div>
                <div>
                  <div className="author-name">John S.</div>
                  <div className="author-title">Busy Professional</div>
                </div>
              </div>
            </div>
            <div className="testimonial-card fade-slide tilt-card" style={{ animationDelay: '0.4s' }}>
              <div className="testimonial-stars">
                {[...Array(5)].map((_, i) => (
                  <FaStarIcon key={i} className="star-icon" />
                ))}
              </div>
              <blockquote className="testimonial-quote">
                "Tracking my elimination diet and mood changes helped me understand my carnivore benefits. My digestive health improved dramatically!"
              </blockquote>
              <div className="testimonial-author">
                <div className="author-avatar">S</div>
                <div>
                  <div className="author-name">Sarah K.</div>
                  <div className="author-title">Wellness Advocate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12 fade-slide">
            <div className="text-xs font-bold text-primary uppercase tracking-widest mb-2">Got questions?</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-text mb-4 tracking-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">Everything you need to know about EatMeat and your carnivore journey</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {[
              { q: 'What makes EatMeat the best carnivore diet app?', a: 'EatMeat combines 5,000+ carnivore and ketovore recipes, meal planning, daily macro tracking, an active community, custom recipe creation, and comprehensive health tracking — all in one app. Track your protein, fat, calories, mood, weight, elimination diet, and more.' },
              { q: 'Can I track my macros and daily nutrition?', a: 'Yes! Our built-in macro tracker lets you log protein, fat, and calories per meal (Breakfast, Lunch, Dinner, Snack). Set daily goals and see real-time progress bars. View monthly stats with charts showing your trends over time.' },
              { q: 'Can I create and share my own recipes?', a: 'Absolutely! Create full custom recipes with ingredients, instructions, and nutritional info. Keep them private for your meal plans or share them with the EatMeat community. Other users can upvote your recipes too!' },
              { q: 'What is the EatMeat Community?', a: 'A private, moderated community where carnivore and ketovore enthusiasts share their journey. Post updates with images, react with emojis, comment on posts, and browse categories like Beginners, Recipes, Transformations, and Q&A. All posts are reviewed by admins.' },
              { q: 'How does the elimination diet calendar work?', a: 'Our unified health tracker lets you log eliminated and added foods, daily mood and energy levels, weight, period cycles, and daily notes — all on one calendar. Track your body\'s response to food changes and identify sensitivities over time.' },
              { q: 'Can I use EatMeat for both carnivore and ketovore diets?', a: 'Yes! When you sign up, choose your preference — Carnivore or Ketovore — and the app tailors recipes, meal plans, and community content to match. You can switch anytime from your profile settings.' },
            ].map((item, i) => (
              <details key={i} className="group bg-gray-50 border border-gray-100 rounded-2xl overflow-hidden fade-slide hover:border-primary/30 transition-colors" style={{ animationDelay: `${0.1 + i * 0.06}s` }}>
                <summary className="flex items-center justify-between cursor-pointer px-6 py-4 list-none">
                  <h3 className="text-base font-bold text-gray-900 pr-4">{item.q}</h3>
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-primary font-bold group-open:rotate-45 transition-transform duration-300">+</span>
                </summary>
                <div className="px-6 pb-5 text-gray-600 leading-relaxed text-sm">{item.a}</div>
              </details>
            ))}
            <div className="text-center pt-6 fade-slide" style={{ animationDelay: '0.5s' }}>
              <Link to="/faq" className="group inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-xl font-semibold shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all">
                View All FAQs
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section — advanced animated */}
      <AdvancedCTASection settings={settings} />

    </div>
  );
});

export default Home;